import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Input, Output, ContentChildren, QueryList, ElementRef, ViewChild, AfterViewInit, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { ButtonIconComponent, ButtonIconInfo } from './button-icon/button-icon.component';
import { ActionHandlerService } from 'src/app/services/action-handler.service';
import { UUID } from 'angular2-uuid';
import { SubScriptionMgr } from 'src/app/model/SubscriptionMgr';
import { ButtonGroupService } from 'src/app/services/button-group.service';

export type ButtonInfo = {
  guiId: string;
  actions: ButtonActions;
  icons: Array<ButtonIconComponent>;
  labels: Array<string>;
  mode: ButtonMode;
  size: ButtonSize;
  toggleable: boolean;
  toggleGroup: string;
  isOn: boolean;
  disabled: boolean;
  maxWidth: string;
  doubleIcon: boolean | 'true';
  isDangerous: boolean;
  layout?: string;
  title?: string;
  useOnLabel?: 'true';
  useOffLabel?: 'true';
}
export type ButtonSize = '' | 'lg' | 'sm' | 'md';
export type ButtonMode = 'warn' | 'danger' | 'primary' | 'secondary' | 'danger-primary' | 'danger-secondary' | '';

export type ButtonActions = { on?: string, off?: string };

@Component({
  selector: 'app-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ButtonComponent implements AfterViewInit, OnDestroy {
  public id: string;
  public buttonActions: ButtonActions = { on: '', off: '' };
  public buttonMode: ButtonMode = '';
  public buttonSize: ButtonSize = '';
  public dropdownToggle: boolean = false;
  public dropdownToggleSplit: boolean = false;
  public dropdownToggleInset: boolean = true;
  public buttonToggleGroup: string;
  public upCaret = false;
  public downCaret = false;
  public leftCaret = false;
  public rightCaret = false;
  public isDangerous = false;

  isToggleable = false;
  ready = false;
  public toggleString = '';
  protected name = '';
  @HostBinding('class.app-btn-group') buttonGroup = false;
  @HostBinding('style.max-width') maxWidthStyle = 'auto';
  @HostBinding('attr.data-top') dataTop = '0px';
  @HostBinding('attr.data-bottom') dataBottom = '0px';
  @HostBinding('attr.data-left') dataLeft = '0px';
  @HostBinding('attr.data-right') dataRight = '0px';
  @HostBinding('class.proxy-only') proxyOnlyStyle = false;

  private _function: string;
  @Input('function')
  set function(f: string) {
    this._function = f;
  }
  get function() {
    return this._function;
  }

  @Input('proxyOnly') set proxyOnly(solo: 'true' | true) {
    this.proxyOnlyStyle = ['true', true].indexOf(solo) > -1;
    this.RefreshView();
  }

  @Input('x-placement') set xPlacement(placement: string) {
    this.button?.nativeElement?.setAttribute('x-placement', placement);
  }
  private _dropdownTitle: string = '';
  @Input('dropdown-title')
  set dropdownTitle(title: string) {
    this._dropdownTitle = title;

    this.RefreshView();
  }
  get dropdownTitle(): string {
    return this._dropdownTitle;
  }
  get xPlacement(): string {
    return this.button?.nativeElement?.getAttribute('x-placement');
  }
  public buttonLayout = '';
  @Input() set layout(lyout: string) {
    this.buttonLayout = lyout;
    this.RefreshView();
  }
  @Input() set buttonName(name: string) {
    this.name = name;
    if ([null, undefined, '', false].indexOf(this.name.trim()) > -1) {
      return;
    }
    this.actionsSvc.RegisterNamedButton(this);
  }
  get buttonName(): string {
    return this.name;
  }
  @Input() set action(action: string) {

    this.buttonActions.on = action;
    this.RefreshView();

  }
  @Input() set offAction(action: string) {
    this.buttonActions.off = action;

  }
  public titleAlign: string = '';
  @Input('data-title-align') set dataTitleAlign(align: string) {
    this.titleAlign = align;
    this.RefreshView();
  }

  private _iconWLabel: boolean = false;
  @Input('iconWLabel')
  set iconWLabel(isSet: 'true' | 'false' | true | false) {
    if (isSet === 'true') isSet = true;
    this._iconWLabel = isSet === true;
    this.RefreshView();
  }
  get iconWLabel(): boolean {
    return this._iconWLabel;
  }

  private _fitted:boolean = false;
  @Input('fitted')
  set fitted(isSet: 'true' | 'false' | true | false) {
    if (isSet === 'true') isSet = true;
    this._fitted = isSet === true;
    this.RefreshView();
  }
  @HostBinding('class.fitted')
  get isFitted(): boolean {
    return this._fitted;
  }

  get dataTitleAlign() {
    return this.titleAlign;
  }
  private _dataTitle: string = '';
  @Input('data-title') set dataTitle(title: string) {
    this._dataTitle = title;
    this.RefreshView();
  }
  @Input() set mode(mode: ButtonMode) {
    if (mode === 'danger') {
      this.isDangerous = true;
      mode = "danger-primary"
    }
    this.buttonMode = mode;
    this.RefreshView();
  }
  get mode(): ButtonMode {
    return this.buttonMode;
  }
  @HostBinding('class.double-icon') isDoubleIcon = false;
  @Input() set doubleIcon(isDouble: 'true' | boolean) {
    this.isDoubleIcon = [true, 'true'].indexOf(isDouble) > -1;
    this.RefreshView();
  }
  get double(): 'true' | boolean {
    return this.isDoubleIcon;
  }
  @Input() set size(btnSize: ButtonSize) {
    this.buttonSize = btnSize;

  }
  @Input() set toggleable(isSet: boolean | string) {
    this.isToggleable = [true, 'true'].indexOf(isSet) > -1;
    this.RefreshView();

  }
  @Input() set toggleGroup(group: string) {
    this.buttonToggleGroup = group;
    this.groups.RegisterGroupButton(this, group);
  }
  get toggleGroup(): string {
    if (!this.buttonToggleGroup) return '';
    return this.buttonToggleGroup;
  }
  @Input() set isOn(isOn: boolean) {
    const danger = this.isDangerous ? 'danger-' : '';
    const wasOn = this.isOn === isOn;
    this.mode = (isOn === true) ? `${danger}secondary` : `${danger}primary`;

    if (!this.isToggleable) return
    if (wasOn === true) {
      return;
    }
    this.isOnChange.emit(this.isOn);
  }
  get isOn(): boolean {
    return this.mode.indexOf('secondary') > -1;;
  }

  @Output()
  public isOnChange = new EventEmitter<boolean>();

  @Input() set classes(classes: string) {
    classes.split(' ').every((value) => {
      this.domNode.nativeElement.classList.toggle(value, true);
    })
  }

  @Input() set reqs(states: string) {
    const reqStates = states.split(' ');
    if (reqStates.length === 0) {
      return;
    }
    this.domNode.nativeElement.classList.toggle('stateful');
    for (const reqState of reqStates) {
      this.domNode.nativeElement.classList.toggle('req-' + reqState, true);
    }
  }


  @Output() change = new EventEmitter<ButtonInfo>();
  @Output() toggles = new EventEmitter<void>();
  @Input('isDropDown') set isDropDown(isDropDown: boolean) {
    this.dropdownToggle = isDropDown;
    this.dropdownToggleSplit = false;
    this.dropdownToggleInset = true;
    this.buttonGroup = true;
    this.RefreshView();
  }
  // tslint:disable-next-line: no-input-rename
  @Input('data-toggle') set dataToggle(toggleString: string) {
    this.toggleString = toggleString;
    // (this.button.nativeElement as HTMLButtonElement).setAttribute('aria-haspopup','true');
    // (this.button.nativeElement as HTMLButtonElement).setAttribute('aria-expanded','false');
  }
  @Input('use-on-label') useOnLabel?: 'true';
  @Input('use-off-label') useOffLabel?: 'true';

  get buttonTitle(): string {
    if (this.action?.indexOf('editor') === 0) {
      console.log(this);
    }
    if (this.isToggleable === true) {
      if (!!this.buttonActions.off) {
        if (this.useOnLabel === undefined) return this.actionsSvc.GetActionAlt(this.isOn ? this.buttonActions.off : this.buttonActions.on);

        if (this.useOnLabel === 'true') return this.actionsSvc.GetActionAlt(this.buttonActions.on);
        if (this.useOffLabel === 'true') return this.actionsSvc.GetActionAlt(this.buttonActions.off)
      }
    }
    if (!this.buttonActions.on) {
      return this._dataTitle;
    }
    return this.actionsSvc.GetActionAlt(this.buttonActions.on);
  }

  @ViewChild('btn') button: ElementRef;
  @ContentChildren(ButtonIconComponent) icons: QueryList<ButtonIconComponent>;

  @Input() set disabled(isDisabled: boolean) {
    if (!this.button) {
      requestAnimationFrame(() => {
        this.disabled = isDisabled;
      });
      return;
    }
    this.button.nativeElement.classList.toggle('deactivated', isDisabled);
    this.RefreshView();
    this.UpdateListeners();

  }

  get disabled(): boolean {
    if (!this.button) {
      return true;
    }
    return this.button.nativeElement.classList.contains('deactivated');
  }
  @Input('maxwidth')
  set maxWidth(size: string) {
    this.maxWidthStyle = size;
    this.RefreshView();
  }


  public get element() {
    return this.domNode.nativeElement;
  }

  get width(): string {
    return this.button.nativeElement.clientWidth + 'px';
  }
  private subscriptions = new SubScriptionMgr();
  Toggle(isOn?: boolean, exec: boolean = true): null | boolean {
    if (!this.isToggleable) return null;

    if (isOn === undefined) {
      isOn = !this.isOn;
    }
    this.isOn = isOn;
    if (!!this.toggleGroup) {
      if (isOn === true) {
        this.groups.ToggleOffOthers(this.toggleGroup, this);
      }
    }
    this.cdr.detectChanges();
    if (exec === true) {
      this.UpdateListeners();
    }
    return this.isOn;
  }
  get groupsService(): ButtonGroupService {
    return this.groups;
  }
  constructor(private cdr: ChangeDetectorRef, private domNode: ElementRef,
    private actionsSvc: ActionHandlerService, private groups: ButtonGroupService) {
    this.id = UUID.UUID();
  }
  CompareButton(button: ButtonComponent) {
    return button.id === this.id;
  }
  Click() {
    if (this.disabled === true) {
      return;
    }
    this.domNode.nativeElement.click();
  }
  HandleClick(_e?: any, internal: boolean = false) {
    if (this.disabled) {
      return;
    }
    if (internal || ['button', 'app-button'].indexOf(_e.target.tagName.toLowerCase()) < 0) {
      let trueCount = 0;
      for (const icon of this.icons) {
        if (_e.target.id === icon.id) {
          trueCount += 1;
          break;
        }
      }
      if (trueCount === 0) {
        return;
      }
    }

    this.Toggle();
    this.TriggerAction();

  }
  TriggerAction() {
    let action: string = this.buttonActions.on;
    if (this.isToggleable === true) {
      if (!this.isOn && (this.buttonActions.off === '')) {
        return;
      }
      if (!!this.buttonActions.off) {
        action = this.isOn ? this.buttonActions.on : this.buttonActions.off;
      }
    }
    if (!!action && (action !== '')) {
      this.actionsSvc.HandleAction(action, { src: 'button' });
    }
  }
  HandleSplitClick(e: MouseEvent) {
    if (e && e.currentTarget) (e.currentTarget as HTMLButtonElement).blur();
    this.toggles.emit();
    e.preventDefault();
    e.stopImmediatePropagation();
  }
  ngAfterViewInit(): void {

    this.ready = true;
    for (const icon of this.icons) {
      this.subscriptions.Subscribe(icon.change, (_event: ButtonIconInfo) => {
        this.UpdateListeners();
      });
    }
    this.domNode.nativeElement.style.setProperty('--num-icons',this.icons.length);
    this.UpdateSides();
    this.UpdateListeners();

  }
  UpdateSides() {
    window.requestAnimationFrame(() => {
      const btn = this.button.nativeElement;
      this.dataTop = btn.offsetTop + 'px';
      this.dataLeft = btn.offsetLeft + 'px';
      this.dataRight = (btn.offsetLeft + btn.clientWidth) + 'px';
      this.dataBottom = (btn.offsetTop + btn.clientHeight) + 'px';
      if (!!this.cdr) {
        this.cdr.detectChanges();
      }
    });
  }
  Clone(): ButtonInfo {
    const buttonIcons = [];
    this.icons.forEach((icon: ButtonIconComponent, _index: number, _src: Array<ButtonIconComponent>) => {
      buttonIcons.push(icon.Clone());
    });
    const buttonLabels = [];
    const labels = Array.from(this.button.nativeElement.getElementsByTagName('label'));
    labels.every((label: HTMLLabelElement, _index: number, _src: Array<HTMLLabelElement>) => {
      buttonLabels.push(label.innerHTML);
      return true;
    });

    return {
      guiId: this.id,
      actions: this.buttonActions,
      icons: buttonIcons,
      labels: (buttonLabels),
      isOn: this.isOn,
      mode: this.buttonMode,
      size: this.buttonSize,
      toggleable: this.isToggleable,
      disabled: this.disabled,
      toggleGroup: this.toggleGroup,
      maxWidth: this.maxWidthStyle,
      doubleIcon: this.isDoubleIcon,
      isDangerous: this.isDangerous,
      layout: this.buttonLayout,
      title: this.buttonTitle,
      useOnLabel: this.useOnLabel,
      useOffLabel: this.useOffLabel
    };
  }
  UpdateListeners() {
    if (this.ready === false) {
      return;
    }
    this.change.emit(this.Clone());
  }
  RefreshView() {
    if (!this.ready) return;
    if (!!this.cdr) {
      this.cdr.detectChanges();
    }

  }
  ngOnDestroy() {
    this.subscriptions.CancelSubscriptions();
    this.groups.UnRegisterGroupButton(this, this.toggleGroup)
    this.cdr.detach();
    delete this.cdr;
  }


}
